<template>
  <div ref="homeView" class="home-view">
    <!-- Back to Top -->
    <button v-if="showBackToTop" class="to-top-button" @click="scrollToTop">
      <i class="bi bi-arrow-bar-up"></i>
    </button>
    <!-- 顶部数据统计数量部分 -->
    <div class="top-static">
      <div class="top-static-item">
        题库数量
        <div class="top-static-item-num">{{ staticData.distinctBelongCount }}</div>
      </div>
      <div class="top-static-item">
        已入库题目数量
        <div class="top-static-item-num">{{ staticData.totalQuestionCount }}</div>
      </div>
      <div class="top-static-item">
        题库题目数量
        <div class="top-static-item-num">{{ staticData.totalTestCount }}</div>
      </div>
      <div class="top-static-item">
        大模型处理题目数量
        <div class="top-static-item-num">{{ staticData.totalTestModelCount }}</div>
      </div>
    </div>
    <!-- 统计数据部分 -->
    <section class="stats-container">
      <div class="stats-wrapper">
        <div class="stats-header">
          <div class="stats-title">
            <h2>题库收录统计</h2>
          </div>
          <div class="type-range-selector" style="margin-bottom: 5px;">
            <button
              v-for="type in typeOptions"
              :key="type.value"
              :class="{ 'btn-active': currentType === type.value }"
              style="cursor: pointer;"
              @click="selectType(type.value)"
            >
              {{ type.label }}
            </button>
          </div>
          <div class="time-range-selector">
            <button
              v-for="range in timeRanges"
              :key="range.value"
              :class="{ 'btn-active': currentRange === range.value }"
              style="cursor: pointer;"
              @click="selectTimeRange(range.value)"
            >
              {{ range.label }}
            </button>
          </div>
        </div>

        <div class="stats-content">
          <div class="chart-container">
            <div ref="statsChart" class="echarts-container"></div>
          </div>

          <div class="stats-table-container">
            <div class="table-header">
              <h3>详细数据</h3>
              <div class="table-summary">
                <span>总计：{{ totalCount }}</span>
                <span>平均：{{ averageCount }}</span>
              </div>
            </div>
            <div class="stats-table-wrapper">
              <el-table
                :data="statsData"
                stripe
                style="width: 100%"
                :header-cell-style="{ backgroundColor: '#f1f4f3' }"
              >
                <el-table-column prop="date" label="日期" />
                <el-table-column
                  v-if="currentType === null"
                  prop="test"
                  label="test数量"
                />
                <el-table-column
                  v-if="currentType === null"
                  prop="test_model"
                  label="test_model数量"
                />
                <el-table-column
                  v-if="currentType === null"
                  label="test/test_model占比(%)"
                >
                  <template slot-scope="scope">
                    {{ calculateTestModelRatio(scope.row.test, scope.row.test_model) }}
                  </template>
                </el-table-column>
                <el-table-column
                  v-if="currentType !== null"
                  prop="count"
                  :label="currentType + '数量'"
                />
                <el-table-column label="操作" width="120">
                  <template slot-scope="scope">
                    <el-button
                      type="primary"
                      size="small"
                      @click="showDailyStats(scope.row.date)"
                    >
                      详情
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 每日详细统计模态框 -->
    <div v-if="dailyStatsModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3>{{ selectedDate }} 详细统计</h3>
          <button class="modal-close" @click="closeDailyStatsModal">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="modal-content">
          <div class="chart-container">
            <div ref="dailyStatsChart" class="echarts-container"></div>
          </div>

          <div class="daily-stats-table-container">
            <div class="table-header">
              <h3>小时统计</h3>
              <div class="table-summary">
                <span>总计：{{ dailyTotalCount }}</span>
                <span>峰值：{{ dailyPeakCount }}</span>
              </div>
            </div>
            <div class="daily-stats-table-wrapper">
              <el-table
                :data="dailyStatsData"
                stripe
                style="width: 100%"
                :header-cell-style="{ backgroundColor: '#f1f4f3' }"
              >
                <el-table-column
                  align="center"
                  prop="hour"
                  label="小时"
                  :formatter="(row, column, cellValue) => `${cellValue}:00`"
                />
                <el-table-column
                  v-if="currentType === null"
                  align="center"
                  prop="test"
                  label="test数量"
                />
                <el-table-column
                  v-if="currentType === null"
                  align="center"
                  prop="test_model"
                  label="test_model数量"
                />
                <el-table-column
                  v-if="currentType === null"
                  align="center"
                  label="test/test_model占比(%)"
                >
                  <template slot-scope="scope">
                    {{ calculateTestModelRatio(scope.row.test, scope.row.test_model) }}
                  </template>
                </el-table-column>
                <el-table-column
                  v-if="currentType !== null"
                  align="center"
                  prop="count"
                  :label="currentType + '数量'"
                />
                <el-table-column
                  v-if="currentType === null"
                  align="center"
                  label="总数占比(%)"
                >
                  <template slot-scope="scope">
                    {{ calculatePercentage(scope.row.test + scope.row.test_model, dailyTotalCount) }}
                  </template>
                </el-table-column>
                <el-table-column
                  v-else
                  align="center"
                  label="总数占比(%)"
                >
                  <template slot-scope="scope">
                    {{ calculatePercentage(scope.row.count, dailyTotalCount) }}
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getDailyTestModelStats, getBelongAndRecordCount } from '@/api/stats'
import { formatDate } from '@/utils/date'

export default {
  name: 'HomeView',
  data() {
    return {
      staticData: {
        distinctBelongCount: 0,
        totalQuestionCount: 0,
        totalTestCount: 0,
        totalTestModelCount: 0
      },
      showBackToTop: false,
      statsChart: null,
      dailyStatsChart: null,
      timeRanges: [
        { label: '全部', value: 'all' },
        { label: '年', value: 'year' },
        { label: '月', value: 'month' },
        { label: '周', value: 'week' }
      ],
      currentRange: 'all',
      typeOptions: [
        { label: '全部', value: null },
        { label: 'test', value: 'test' },
        { label: 'test_model', value: 'test_model' }
      ],
      currentType: null,
      statsData: [],
      dailyStatsModal: false,
      dailyStatsData: [],
      selectedDate: '',
      totalCount: 0,
      averageCount: 0,
      dailyTotalCount: 0,
      dailyPeakCount: 0
    };
  },
  mounted() {
    if (this.$refs.homeView) {
      this.$refs.homeView.addEventListener('scroll', this.handleScroll);
    }
    this.initStatsChart()
    this.getStaticData()
    this.fetchStatsData()
  },
  beforeDestroy() {
    if (this.$refs.homeView) {
      this.$refs.homeView.removeEventListener('scroll', this.handleScroll);
    }
  },
  methods: {
    getStaticData() {
      getBelongAndRecordCount().then(res => {
        console.log('res：：：+ ', res)
        this.staticData = res.data
      })
    },
    scrollToTop() {
      if (this.$refs.homeView) {
        this.$refs.homeView.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    },
    handleScroll() {
      if (this.$refs.homeView) {
        this.showBackToTop = this.$refs.homeView.scrollTop > 0;
      }
    },
    initStatsChart() {
      this.statsChart = echarts.init(this.$refs.statsChart)
    },
    async fetchStatsData() {
      const loading = this.$loading({
        lock: true,
        text: '数据加载中',
        spinner: 'el-icon-loading',
        background: this.$store.state.isDark ? 'rgba(30, 30, 30, 0.7)' : 'rgba(255, 255, 255, 0.7)'
      });
      const { startTime, endTime } = this.calculateDateRange()
      try {
        // 如果选择了全部，则获取两种类型的数据
        const types = this.currentType === null
          ? ['test', 'test_model']
          : [this.currentType];

        // 并行请求
        const statsPromises = types.map(type =>
          getDailyTestModelStats({
            type,
            startTime,
            endTime
          })
        );
        const responses = await Promise.all(statsPromises);

        // 合并或处理数据
        if (this.currentType === null) {
          // 合并两种类型的数据
          const combinedStats = {};
          let totalCount = 0;

          responses.forEach((response, index) => {
            const typeName = types[index];
            response.data.stats.forEach(item => {
              if (!combinedStats[item.date]) {
                combinedStats[item.date] = {
                  date: item.date,
                  test: 0,
                  test_model: 0,
                  count: 0 // 添加总数字段
                };
              }
              combinedStats[item.date][typeName] += item.count;
              combinedStats[item.date].count += item.count; // 计算总数
            });
            totalCount += response.data.date_total || 0;
          });

          // 转换为数组并排序
          this.statsData = Object.values(combinedStats)
          this.totalCount = totalCount;
        } else {
          // 单一类型处理
          this.statsData = responses[0].data.stats || [];
          this.totalCount = responses[0].data.date_total || 0;
        }

        this.averageCount = this.statsData.length > 0
          ? Math.round(this.totalCount / this.statsData.length)
          : 0;

        this.renderStatsChart();
      } catch (error) {
        console.error('获取统计数据失败', error);
      } finally {
        loading.close()
      }
    },
    renderStatsChart() {
      // 如果图表未初始化，先初始化
      if (!this.statsChart) {
        this.statsChart = echarts.init(this.$refs.statsChart);
      }

      // 清除之前的图表
      this.statsChart.clear();

      const option = {
        title: {
          text: '',
          left: 'center',
          textStyle: {
            color: '#666',
            fontWeight: 'bold',
            fontSize: '22px',
            fontFamily: 'fangsong'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.statsData.map(item => item.date),
          axisLine: {
            lineStyle: {
              color: '#999'
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            lineStyle: {
              color: '#999'
            }
          }
        },
        legend: {
          data: this.currentType === null ? ['test', 'test_model'] : [this.currentType],
          top: 'top'
        },
        series: this.currentType === null
          ? [
              {
                name: 'test',
                type: 'bar',
                data: this.statsData.map(item => item.test || 0),
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    {offset: 0, color: '#83bff6'},
                    {offset: 0.5, color: '#188df0'},
                    {offset: 1, color: '#188df0'}
                  ])
                }
              },
              {
                name: 'test_model',
                type: 'bar',
                data: this.statsData.map(item => item.test_model || 0),
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    {offset: 0, color: '#ff7f50'},
                    {offset: 0.5, color: '#ff4500'},
                    {offset: 1, color: '#ff4500'}
                  ])
                }
              }
            ]
          : [{
              name: this.currentType,
              type: 'bar',
              barWidth: '60%',
              data: this.statsData.map(item => item.count),
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  {offset: 0, color: '#83bff6'},
                  {offset: 0.5, color: '#188df0'},
                  {offset: 1, color: '#188df0'}
                ])
              }
            }]
      }

      // 设置新的图表选项
      this.statsChart.setOption(option);
    },
    calculateDateRange() {
      const now = new Date()
      let startTime, endTime

      switch (this.currentRange) {
        case 'all': {
          startTime = null
          endTime = null
          break
        }
        case 'year': {
          startTime = `${now.getFullYear()}-01-01`
          endTime = formatDate(now)
          break
        }
        case 'month': {
          const lastMonth = new Date(now.getFullYear(), now.getMonth(), 1)
          startTime = formatDate(lastMonth)
          endTime = formatDate(now)
          break
        }
        case 'week': {
          const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          startTime = formatDate(lastWeek)
          endTime = formatDate(now)
          break
        }
      }

      return { startTime, endTime }
    },
    selectTimeRange(range) {
      this.currentRange = range
      this.fetchStatsData()
    },
    selectType(type) {
      this.currentType = type;
      this.fetchStatsData();
    },
    async showDailyStats(date) {
      this.selectedDate = date
      try {
        // 如果当前类型为 null，获取两种类型的数据
        const types = this.currentType === null
          ? ['test', 'test_model']
          : [this.currentType];

        // 并行请求
        const statsPromises = types.map(type =>
          getDailyTestModelStats({
            type,
            date
          })
        );

        const responses = await Promise.all(statsPromises);

        // 合并或处理数据
        if (this.currentType === null) {
          // 合并两种类型的数据
          const combinedStats = {};
          let totalCount = 0;

          responses.forEach((response, index) => {
            const typeName = types[index];
            response.data.stats.forEach(item => {
              if (!combinedStats[item.hour]) {
                combinedStats[item.hour] = {
                  hour: item.hour,
                  test: 0,
                  test_model: 0,
                  count: 0 // 添加总数字段
                };
              }
              combinedStats[item.hour][typeName] += item.count;
              combinedStats[item.hour].count += item.count; // 计算总数
            });
            totalCount += response.data.stats.reduce((sum, item) => sum + item.count, 0);
          });

          // 转换为数组并排序
          this.dailyStatsData = Object.values(combinedStats)
            .sort((a, b) => a.hour - b.hour);
          this.dailyTotalCount = totalCount;
        } else {
          // 单一类型处理
          this.dailyStatsData = responses[0].data.stats || [];
          this.dailyTotalCount = this.dailyStatsData.reduce((sum, item) => sum + item.count, 0);
        }

        this.dailyPeakCount = Math.max(...this.dailyStatsData.map(item =>
          this.currentType === null
            ? (item.test || 0) + (item.test_model || 0)
            : item.count
        ), 0);

        this.dailyStatsModal = true
        this.$nextTick(() => {
          this.renderDailyStatsChart(date)
        })
      } catch (error) {
        console.error('获取每日统计数据失败', error)
      }
    },
    renderDailyStatsChart(date) {
      // 如果图表未初始化，先初始化
      if (!this.dailyStatsChart) {
        this.dailyStatsChart = echarts.init(this.$refs.dailyStatsChart);
      }

      // 清除之前的图表
      this.dailyStatsChart.clear();

      const option = {
        title: {
          text: '',
          left: 'center',
          textStyle: {
            color: '#333',
            fontWeight: 'bold'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.dailyStatsData.map(item => item.hour),
          axisLine: {
            lineStyle: {
              color: '#999'
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            lineStyle: {
              color: '#999'
            }
          }
        },
        legend: {
          data: this.currentType === null ? ['test', 'test_model'] : [this.currentType],
          top: 'top'
        },
        series: this.currentType === null
          ? [
              {
                name: 'test',
                type: 'bar',
                data: this.dailyStatsData.map(item => item.test || 0),
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    {offset: 0, color: '#83bff6'},
                    {offset: 0.5, color: '#188df0'},
                    {offset: 1, color: '#188df0'}
                  ])
                }
              },
              {
                name: 'test_model',
                type: 'bar',
                data: this.dailyStatsData.map(item => item.test_model || 0),
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    {offset: 0, color: '#ff7f50'},
                    {offset: 0.5, color: '#ff4500'},
                    {offset: 1, color: '#ff4500'}
                  ])
                }
              }
            ]
          : [{
              name: this.currentType,
              type: 'bar',
              barWidth: '60%',
              data: this.dailyStatsData.map(item => item.count),
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  {offset: 0, color: '#83bff6'},
                  {offset: 0.5, color: '#188df0'},
                  {offset: 1, color: '#188df0'}
                ])
              }
            }]
      }

      // 设置新的图表选项
      this.dailyStatsChart.setOption(option);

      // 使用 date 参数，避免未使用警告
      console.log(`渲染 ${date} 的每小时统计图表`);
    },
    calculatePercentage(count, total) {
      return total > 0 ? ((count / total) * 100).toFixed(1) : 0
    },
    calculateTestModelRatio(test, test_model) {
      // 计算 test 占 test_model 的占比
      if (test_model === 0) return 0;
      return ((test / test_model) * 100).toFixed(1);
    },
    closeDailyStatsModal() {
      this.dailyStatsModal = false
    }
  }
};
</script>

<style lang="scss" scoped>
.home-view {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background-color: var(--bg-secondary, #f9fafb);
  padding: 2rem;

  .top-static {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    margin-bottom: 2rem;

    &-item {
      padding: 1.5rem;
      background-color: var(--card-bg, #fff);
      border-radius: 0.75rem;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;

      &-num {
        font-size: 2rem;
        margin-top: 0.5rem;
        color: dodgerblue;
        font-family: 'Courier New', Courier, monospace;
      }
    }
  }

  // Hero Section
  .hero-section {
    position: relative;
    height: 500px;
    overflow: hidden;
    background: linear-gradient(to bottom right, #1f2937, #1e40af, #7e22ce);

    .hero-content {
      position: relative;
      margin: 0 auto;
      padding: 0 1rem;
      height: 100%;
      display: flex;
      align-items: center;

      .hero-text {
        max-width: 512px;

        h1 {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #fff;
          line-height: 1.2;

          span {
            background: linear-gradient(to right, #60a5fa, #22d3ee);
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
          }
        }

        p {
          font-size: 1.125rem;
          margin-bottom: 2rem;
          color: #d1d5db;
          max-width: 48rem;
          line-height: 1.6;
          font-weight: 300;
        }

        .hero-buttons {
          display: flex;
          gap: 1rem;

          button {
            padding: 0.75rem 2rem;
            border-radius: 0.5rem;
            white-space: nowrap;
            transition: all 0.3s;
            font-size: 1rem;
            box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);

            &:first-child {
              background: linear-gradient(to right, #2563eb, #3b82f6);
              color: #fff;

              &:hover {
                background: linear-gradient(to right, #1d4ed8, #2563eb);
                box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.3);
              }
            }

            &:last-child {
              background-color: rgba(255, 255, 255, 0.1);
              backdrop-filter: blur(4px);
              color: #fff;
              border: 1px solid rgba(255, 255, 255, 0.2);

              &:hover {
                background-color: #fff;
                color: #1e40af;
                border-color: #fff;
              }
            }
          }
        }
      }
    }
  }

  // Stats Section
  .stats-container {
    padding: 2rem 0;
    background-color: var(--bg-secondary, #f9fafb);

    .stats-wrapper {
      margin: 0 auto;
      padding: 0 1rem;

      .stats-header {
        text-align: center;
        margin-bottom: 2rem;

        .stats-title {
          margin-bottom: 1.5rem;

          h2 {
            font-size: 2rem;
            font-weight: 700;
            margin-bottom: 0.5rem;
          }

          p {
            color: var(--text-secondary, #6b7280);
          }
        }

        .type-range-selector, .time-range-selector {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          flex-wrap: wrap;

          button {
            padding: 0.5rem 1rem;
            border: 1px solid #3498db;
            background-color: transparent;
            color: #3498db;
            border-radius: 0.5rem;
            transition: all 0.3s ease;
            font-size: 0.9rem;

            &:hover, &.btn-active {
              background-color: #3498db;
              color: white;
            }
          }
        }
      }

      .stats-content {
        .chart-container {
          .echarts-container {
            width: 100%;
            height: 400px;
            border-radius: 0.75rem;
            overflow: hidden;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }
        }

        .stats-table-container {
          margin-top: 2rem;

          .table-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;

            h3 {
              font-size: 1.25rem;
              color: var(--text-primary, #2c3e50);
            }

            .table-summary {
              display: flex;
              gap: 1rem;
              color: var(--text-secondary, #7f8c8d);

              span {
                font-size: 0.9rem;
              }
            }
          }

          .stats-table-wrapper {
            .el-table {
              border-radius: 0.75rem;
              overflow: hidden;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            }
          }
        }
      }
    }
  }

  // Daily Stats Modal
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;

    .modal-container {
      background-color: white;
      border-radius: 1rem;
      width: 90%;
      max-width: 1000px;
      max-height: 90vh;
      display: flex;
      flex-direction: column;
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
      overflow: hidden;

      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;
        background-color: #f1f4f3;
        border-bottom: 1px solid #e0e6e3;

        h3 {
          font-size: 1.25rem;
          color: var(--text-primary, #2c3e50);
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          color: var(--text-secondary, #7f8c8d);
          cursor: pointer;
          transition: color 0.3s ease;

          &:hover {
            color: var(--text-primary, #2c3e50);
          }
        }
      }

      .modal-content {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        padding: 1.5rem;
        overflow-y: auto;

        .chart-container {
          .echarts-container {
            width: 100%;
            height: 300px;
            border-radius: 0.75rem;
            overflow: hidden;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }
        }

        .daily-stats-table-container {
          .table-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;

            h3 {
              font-size: 1.25rem;
              color: var(--text-primary, #2c3e50);
            }

            .table-summary {
              display: flex;
              gap: 1rem;
              color: var(--text-secondary, #7f8c8d);

              span {
                font-size: 0.9rem;
              }
            }
          }

          .daily-stats-table-wrapper {
            .el-table {
              border-radius: 0.75rem;
              overflow: hidden;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            }
          }
        }
      }
    }
  }

  // 响应式设计
  @media screen and (max-width: 768px) {
    .top-static {
      grid-template-columns: repeat(2, 1fr);
    }

    .hero-section {
      height: 400px;

      .hero-text {
        h1 {
          font-size: 2.5rem;
        }

        p {
          font-size: 1rem;
        }
      }
    }

    .stats-container {
      padding: 1rem;

      .stats-wrapper {
        padding: 1rem;

        .stats-header {
          .stats-title {
            h2 {
              font-size: 1.75rem;
            }
          }

          .type-range-selector, .time-range-selector {
            gap: 0.5rem;

            button {
              padding: 0.4rem 0.8rem;
              font-size: 0.8rem;
            }
          }
        }

        .stats-content {
          .chart-container {
            .echarts-container {
              height: 250px;
            }
          }
        }
      }
    }

    .modal-overlay {
      .modal-container {
        .modal-content {
          .chart-container {
            .echarts-container {
              height: 250px;
            }
          }
        }
      }
    }
  }
}
</style>